import { defaultBlocksWithSavedGeneric, ownSavedBlocks } from "./_content-blocks.ts";
import { renderSignatureBlockHtml } from "./_signature-block.ts";

// --- App origin for in-email links (view page, etc.) ---
const _resolvedOrigin = "https://helpgenie.ai";

const ASSET_BASE = "https://vdskjkjxyeehklwxnhrf.supabase.co/storage/v1/object/public/email-assets";

// --- Types (mirrors src/types/email-templates.ts) ---

interface ContentBlock {
  id: string;
  type: string;
  enabled: boolean;
  order: number;
  label?: string;
  settings?: Record<string, any>;
}

interface ViewReportConfig {
  version?: number;
  colors?: {
    primary?: string;
    accent?: string;
    text?: string;
    background?: string;
    headerColor?: string;
    sectionBackground?: string;
  };
  fonts?: {
    headingSize?: number;
    googleFont?: "Poppins" | "Roboto" | "Inter" | "Lato" | "Open Sans";
    family?: string;
  };
  branding?: {
    logoUrl?: string;
    companyName?: string;
  };
  buttons?: {
    buttonStyle?: "rounded" | "square" | "pill";
    borderRadius?: number;
    shadowEnabled?: boolean;
    buttonText?: string;
  };
  blocks?: ContentBlock[];
}

const googleFontMap: Record<string, string> = {
  Poppins: "'Poppins', 'Segoe UI', Arial, sans-serif",
  Roboto: "'Roboto', 'Segoe UI', Arial, sans-serif",
  Inter: "'Inter', 'Segoe UI', Arial, sans-serif",
  Lato: "'Lato', 'Segoe UI', Arial, sans-serif",
  "Open Sans": "'Open Sans', 'Segoe UI', Arial, sans-serif",
};

interface FlaggedRow {
  rowIdx: number;
  reason: string;
  conversationId?: string | number | null;
}

interface ViewReportData {
  viewName: string;
  periodStart: string;
  periodEnd: string;
  rowCount: number;
  takeaways: string;
  flaggedRows?: FlaggedRow[];
  timezone?: string;
  /** No single genie owns a view report — team branding is the only source
   * for the Email Signature block here. GenieBranding jsonb from `teams.branding`. */
  teamBranding?: Record<string, any> | null;
  teamName?: string | null;
}

const DEFAULT_LABELS: Record<string, string> = {
  report_data_summary: "Report Summary",
  report_key_takeaways: "Key Takeaways",
  report_flagged_rows: "Rows to Pay Attention To",
  mobile_app_promo: "Help Genie in your pocket",
};

function brandHexToRgba(hex: string, alpha: number): string {
  const clean = hex.replace("#", "");
  const r = parseInt(clean.substr(0, 2), 16);
  const g = parseInt(clean.substr(2, 2), 16);
  const b = parseInt(clean.substr(4, 2), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

function resolveBlocks(config: ViewReportConfig | null | undefined): ContentBlock[] {
  // Signature depends on team branding a tenant may not have configured, so
  // it starts OFF when backfilled — unlike the rest of the value layer.
  const VALUE_LAYER_AUTOINJECT: string[] = ["signature"];
  const VALUE_LAYER_DEFAULT_DISABLED = new Set(["signature"]);

  const defaults: ContentBlock[] = [
    { id: "report_data_summary", type: "report_data_summary", enabled: true, order: 0 },
    { id: "report_key_takeaways", type: "report_key_takeaways", enabled: true, order: 1 },
    { id: "report_flagged_rows", type: "report_flagged_rows", enabled: true, order: 2 },
    { id: "mobile_app_promo", type: "mobile_app_promo", enabled: true, order: 3 },
    { id: "signature", type: "signature", enabled: false, order: 4 },
  ];

  const savedBlocks = ownSavedBlocks(config, defaults);
  if (!savedBlocks) return defaultBlocksWithSavedGeneric(config, defaults);

  const blocks = [...savedBlocks].sort((a, b) => a.order - b.order);
  const existing = new Set(blocks.map((b) => b.type));
  let maxOrder = blocks.length > 0 ? Math.max(...blocks.map((b) => b.order)) : -1;

  for (const type of VALUE_LAYER_AUTOINJECT) {
    if (existing.has(type)) continue;
    maxOrder += 1;
    blocks.push({ id: type, type, enabled: !VALUE_LAYER_DEFAULT_DISABLED.has(type), order: maxOrder });
    existing.add(type);
  }

  blocks.sort((a, b) => a.order - b.order);
  return pinSignatureLast(blocks);
}

// The signature depends on genie/team branding and reads as a sign-off — it
// must always render as the final section, regardless of where `order`
// (user-editable via drag-and-drop reorder in the Report Sections UI, or a
// stale saved config) places it. Sorting by `order` alone isn't enough to
// guarantee that, so pin it last as an explicit final step.
function pinSignatureLast(blocks: ContentBlock[]): ContentBlock[] {
  const signatureIndex = blocks.findIndex((b) => b.type === "signature");
  if (signatureIndex === -1 || signatureIndex === blocks.length - 1) return blocks;
  const withoutSignature = blocks.filter((b) => b.type !== "signature");
  return [...withoutSignature, blocks[signatureIndex]];
}

interface StyleContext {
  colors: {
    primary: string;
    accent: string;
    text: string;
    background: string;
    headerColor: string;
    sectionBackground: string;
  };
  fontFamily: string;
  borderRadius: number;
  metricButtonRadius: string;
  boxShadow: string;
  shadowEnabled: boolean;
}

function formatDateRange(start: string, end: string, timezone = "UTC"): string {
  try {
    const formatter = new Intl.DateTimeFormat("en-US", { timeZone: timezone, month: "short", day: "numeric", year: "numeric" });
    return `${formatter.format(new Date(start))} — ${formatter.format(new Date(end))}`;
  } catch {
    return `${start} — ${end}`;
  }
}

function renderDataSummary(block: ContentBlock, data: ViewReportData, styles: StyleContext): string {
  const { colors, fontFamily, metricButtonRadius } = styles;
  const label = block.label || DEFAULT_LABELS.report_data_summary;

  return `
    <tr>
      <td style="padding: 0 24px 10px 24px;">
        <table cellpadding="0" cellspacing="0" border="0" width="100%" style="background:#FFFFFF;border:1px solid #E5EAF0;border-radius:${metricButtonRadius};overflow:hidden;">
          <tr>
            <td style="height:2px;background:${colors.primary};line-height:2px;font-size:0;">&nbsp;</td>
          </tr>
          <tr>
            <td style="padding:12px 16px 10px 16px;">
              <h3 style="color:${colors.headerColor};margin:0 0 8px 0;font-size:13px;font-weight:600;font-family:${fontFamily};text-transform:uppercase;letter-spacing:0.4px;">${label}</h3>
              <div style="font-size:11px;color:#6B7280;font-family:${fontFamily};margin-bottom:10px;">${formatDateRange(data.periodStart, data.periodEnd, data.timezone)}</div>
              <table cellpadding="0" cellspacing="0" border="0" width="100%">
                <tr>
                  <td width="50%" style="padding: 3px;" valign="top">
                    <table cellpadding="0" cellspacing="0" border="0" width="100%" style="background: ${colors.primary}0D; border: 1px solid ${colors.primary}40; border-radius: 6px;">
                      <tr>
                        <td style="padding: 9px 11px;">
                          <div style="font-size: 19px; font-weight: 800; color: ${colors.primary}; line-height: 1; font-family: 'Baloo Paaji 2', ${fontFamily};">${data.rowCount}</div>
                          <div style="font-size: 9px; color: #6B7280; font-weight: 700; text-transform: uppercase; letter-spacing: 0.5px; margin-top: 3px;">Rows this period</div>
                        </td>
                      </tr>
                    </table>
                  </td>
                </tr>
              </table>
            </td>
          </tr>
        </table>
      </td>
    </tr>`;
}

function renderKeyTakeaways(block: ContentBlock, data: ViewReportData, styles: StyleContext): string {
  if (!data.takeaways) return "";
  const { colors, fontFamily, metricButtonRadius } = styles;
  const label = block.label || DEFAULT_LABELS.report_key_takeaways;

  return `
    <tr>
      <td style="padding: 0 24px 10px 24px;">
        <table cellpadding="0" cellspacing="0" border="0" width="100%" style="background:#FFFFFF;border:1px solid #E5EAF0;border-radius:${metricButtonRadius};overflow:hidden;">
          <tr>
            <td style="height:2px;background:${colors.primary};line-height:2px;font-size:0;">&nbsp;</td>
          </tr>
          <tr>
            <td style="padding:12px 16px 12px 16px;">
              <h3 style="color:${colors.headerColor};margin:0 0 8px 0;font-size:13px;font-weight:600;font-family:${fontFamily};text-transform:uppercase;letter-spacing:0.4px;">${label}</h3>
              <div style="font-size:12px;color:${colors.text};font-family:${fontFamily};line-height:1.5;">${data.takeaways}</div>
            </td>
          </tr>
        </table>
      </td>
    </tr>`;
}

function renderFlaggedRows(block: ContentBlock, data: ViewReportData, styles: StyleContext): string {
  const flagged = data.flaggedRows || [];
  if (flagged.length === 0) return "";
  const { colors, fontFamily, metricButtonRadius } = styles;
  const label = block.label || DEFAULT_LABELS.report_flagged_rows;

  const rows = flagged
    .slice(0, 25)
    .map(
      (f) => `
        <tr>
          <td style="padding-bottom:6px;">
            <table cellpadding="0" cellspacing="0" border="0" width="100%" style="background:${brandHexToRgba("#DC2626", 0.05)};border-left:3px solid #DC2626;border-radius:6px;">
              <tr>
                <td style="padding:9px 12px;">
                  <div style="font-size:11px;color:#6B7280;font-family:${fontFamily};margin-bottom:2px;">Row ${f.rowIdx + 1}</div>
                  <div style="font-size:12px;color:${colors.text};font-family:${fontFamily};line-height:1.4;">${f.reason}</div>
                  ${
                    f.conversationId
                      ? `<a href="${_resolvedOrigin}/genie-hub/conversations/${f.conversationId}" style="font-size:11px;color:${colors.primary};text-decoration:none;font-family:${fontFamily};">View conversation &rarr;</a>`
                      : ""
                  }
                </td>
              </tr>
            </table>
          </td>
        </tr>`,
    )
    .join("");

  return `
    <tr>
      <td style="padding: 0 24px 10px 24px;">
        <table cellpadding="0" cellspacing="0" border="0" width="100%" style="background:#FFFFFF;border:1px solid #E5EAF0;border-radius:${metricButtonRadius};overflow:hidden;">
          <tr>
            <td style="height:2px;background:${colors.primary};line-height:2px;font-size:0;">&nbsp;</td>
          </tr>
          <tr>
            <td style="padding:12px 16px 10px 16px;">
              <h3 style="color:${colors.headerColor};margin:0 0 8px 0;font-size:13px;font-weight:600;font-family:${fontFamily};text-transform:uppercase;letter-spacing:0.4px;">${label}</h3>
              <table cellpadding="0" cellspacing="0" border="0" width="100%">${rows}</table>
            </td>
          </tr>
        </table>
      </td>
    </tr>`;
}

function renderMobileAppPromo(block: ContentBlock, _data: ViewReportData, styles: StyleContext): string {
  const settings = block.settings || {};
  const { colors, fontFamily, metricButtonRadius } = styles;
  const heading = settings.heading || "Share Help Genie with your customers";
  const body = settings.body || "Get your team and customers on the app for instant support on every product.";
  const appStoreUrl = settings.appStoreUrl || "https://helpgenie.ai/app";
  const playStoreUrl = settings.playStoreUrl || "https://helpgenie.ai/app";
  const headerColor = colors.headerColor || colors.text || "#28406F";

  return `
    <tr>
      <td style="padding: 6px 24px 14px 24px;">
        <table cellpadding="0" cellspacing="0" border="0" width="100%" style="background: #FFFFFF; border: 1px solid #E5EAF0; border-radius: ${metricButtonRadius}; overflow: hidden;">
          <tr>
            <td style="height: 2px; background: ${colors.primary}; line-height: 2px; font-size: 0;">&nbsp;</td>
          </tr>
          <tr>
            <td style="padding: 12px 14px;" valign="middle">
              <div style="color: ${headerColor}; font-size: 14px; font-weight: 700; font-family: 'Baloo Paaji 2', ${fontFamily}; margin: 0 0 2px 0; line-height: 1.2;">${heading}</div>
              <div style="color: #6B7280; font-size: 12px; line-height: 1.4; font-family: ${fontFamily}; margin: 0 0 10px 0;">${body}</div>
              <table cellpadding="0" cellspacing="0" border="0">
                <tr>
                  <td style="padding-right: 8px;">
                    <a href="${appStoreUrl}" style="display: inline-block;">
                      <img src="${ASSET_BASE}/app-store-badge.svg" alt="Download on the App Store" width="120" height="36" style="display: block; width: 120px; height: 36px; border: 0;" />
                    </a>
                  </td>
                  <td>
                    <a href="${playStoreUrl}" style="display: inline-block;">
                      <img src="${ASSET_BASE}/google-play-badge.png" alt="Get it on Google Play" width="120" height="36" style="display: block; width: 120px; height: 36px; border: 0;" />
                    </a>
                  </td>
                </tr>
              </table>
            </td>
          </tr>
        </table>
      </td>
    </tr>`;
}

function renderSignature(_block: ContentBlock, data: ViewReportData, _styles: StyleContext): string {
  const html = renderSignatureBlockHtml(data.teamName || "Help Genie", null, data.teamBranding);
  if (!html) return "";
  return `<tr><td style="padding: 6px 24px 14px 24px;">${html}</td></tr>`;
}

const blockRenderers: Record<
  string,
  (block: ContentBlock, data: ViewReportData, styles: StyleContext) => string
> = {
  report_data_summary: renderDataSummary,
  report_key_takeaways: renderKeyTakeaways,
  report_flagged_rows: renderFlaggedRows,
  mobile_app_promo: renderMobileAppPromo,
  signature: renderSignature,
};

export function generateViewReportEmailConfigurable(
  data: ViewReportData,
  config?: ViewReportConfig | null,
): string {
  const defaults = {
    colors: {
      primary: "#4E9CFF",
      accent: "#FF4DB8",
      text: "#1A1A1A",
      background: "#F9FAFC",
      headerColor: undefined as string | undefined,
    },
    fonts: {
      family: "'Poppins', 'Segoe UI', Arial, sans-serif",
      headingSize: 32,
    },
    branding: {
      logoUrl: `${ASSET_BASE}/wordmark.png`,
      companyName: "Help Genie",
    },
    buttons: {
      buttonStyle: "rounded" as const,
      borderRadius: 8,
      shadowEnabled: true,
      buttonText: "View Report",
    },
  };

  const colors = { ...defaults.colors, ...config?.colors };
  const fonts = { ...defaults.fonts, ...config?.fonts };
  const branding = { ...defaults.branding, ...config?.branding };
  const buttons = { ...defaults.buttons, ...config?.buttons };

  const googleFontName = fonts.googleFont || "Poppins";
  const fontFamily = fonts.family || googleFontMap[googleFontName];
  const googleFontUrl = `https://fonts.googleapis.com/css2?family=${googleFontName}:wght@400;500;600;700&family=Baloo+Paaji+2:wght@700;800&display=swap`;

  const getButtonRadius = () => {
    switch (buttons.buttonStyle) {
      case "square":
        return "0px";
      case "pill":
        return "999px";
      case "rounded":
      default:
        return `${buttons.borderRadius}px`;
    }
  };

  const headerTextColor = colors.headerColor || colors.text || "#28406F";
  const boxShadow = buttons.shadowEnabled ? `0 4px 20px ${brandHexToRgba(colors.primary || defaults.colors.primary, 0.1)}` : "none";
  const metricButtonRadius = getButtonRadius();

  const styleContext: StyleContext = {
    colors: {
      primary: colors.primary || defaults.colors.primary,
      accent: colors.accent || defaults.colors.accent,
      text: colors.text || defaults.colors.text,
      background: colors.background || defaults.colors.background,
      headerColor: headerTextColor,
      sectionBackground: colors.sectionBackground || "#FAFBFC",
    },
    fontFamily,
    borderRadius: buttons.borderRadius || defaults.buttons.borderRadius,
    metricButtonRadius,
    boxShadow,
    shadowEnabled: buttons.shadowEnabled ?? defaults.buttons.shadowEnabled,
  };

  const blocks = resolveBlocks(config);
  const enabledBlocks = blocks.filter((b) => b.enabled);

  const blocksHtml = enabledBlocks
    .map((block) => {
      const renderer = blockRenderers[block.type];
      if (!renderer) return "";
      return renderer(block, data, styleContext);
    })
    .filter(Boolean)
    .join("\n");

  // The team's own branded signature (when configured) replaces the fixed
  // Help Genie footer below rather than stacking under it.
  const hasCustomSignature = Boolean(
    enabledBlocks.some((b) => b.type === "signature") && data.teamBranding?.signatureStyle?.enabled,
  );

  return `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="utf-8">
        <meta name="viewport" content="width=device-width, initial-scale=1.0">
        <title>View Report</title>
        <link rel="stylesheet" href="${googleFontUrl}" type="text/css">
        <style>@import url('${googleFontUrl}');</style>
      </head>
      <body style="margin: 0; padding: 0; background-color: #F5F5F5; font-family: ${fontFamily}; -webkit-text-size-adjust: 100%; -ms-text-size-adjust: 100%;">
        <table cellpadding="0" cellspacing="0" border="0" width="100%" style="background-color: #F5F5F5; margin: 0; padding: 0;">
          <tr>
            <td align="center" style="padding: 12px;">
              <table cellpadding="0" cellspacing="0" border="0" width="600" style="max-width: 600px; background-color: ${styleContext.colors.background}; box-shadow: ${boxShadow}; border-radius: ${styleContext.borderRadius}px; overflow: hidden;">
                <tr>
                  <td style="background-color: ${styleContext.colors.background}; padding: 20px 24px 4px 24px;">
                    <img src="${branding.logoUrl}" alt="${branding.companyName}" style="max-height: 28px; max-width: 160px; display: block;" />
                  </td>
                </tr>
                <tr>
                  <td style="background-color: ${styleContext.colors.background}; padding: 10px 24px 16px 24px;">
                    <h1 style="color: ${headerTextColor}; margin: 0; font-size: ${Math.max(20, (fonts.headingSize ?? 32) - 8)}px; font-weight: 700; font-family: 'Baloo Paaji 2', ${fontFamily}; line-height: 1.15; letter-spacing: -0.2px;">${data.viewName} — View Report</h1>
                    <div style="width: 44px; height: 2px; background: ${styleContext.colors.primary}; margin: 8px 0 0; border-radius: 2px;"></div>
                  </td>
                </tr>

                ${blocksHtml}

                ${hasCustomSignature ? "" : `
                <tr>
                  <td align="center" style="padding: 18px 24px; background: ${styleContext.colors.primary};">
                    <div style="color: #FFFFFF; font-size: 18px; font-weight: 700; font-family: 'Baloo Paaji 2', ${fontFamily}; letter-spacing: 0.2px; margin: 0 0 4px 0;">
                      ${branding.companyName}
                    </div>
                    <div style="color: rgba(255,255,255,0.96); font-size: 12px; font-weight: 500; font-family: ${fontFamily}; margin-bottom: 10px;">
                      Goodbye manuals. Hello Help Genie.
                    </div>
                    <div style="color: rgba(255,255,255,0.85); font-size: 11px; font-family: ${fontFamily};">
                      <a href="https://helpgenie.ai" style="color: #FFFFFF; text-decoration: none; font-weight: 500;">helpgenie.ai</a>
                      &nbsp;&nbsp;&middot;&nbsp;&nbsp; +64 9 886 4856
                    </div>
                  </td>
                </tr>
                `}
              </table>
            </td>
          </tr>
        </table>
      </body>
      </html>
    `;
}
